import React, { useEffect, useState } from "react";
import { deleteFeedback } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";

const FeedbackAdminPanel = () => {
  const {
    feedbacks = [],
    feedbacksLoading = false,
    fetchFeedbacks,
  } = useGlobalData();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [darkMode, setDarkMode] = useState(document.body.classList.contains("dark-mode"));
  const role = localStorage.getItem("role");

  // Sync dark mode with Navbar
  useEffect(() => {
    const handler = () => setDarkMode(document.body.classList.contains("dark-mode"));
    window.addEventListener("darkModeChanged", handler);
    return () => window.removeEventListener("darkModeChanged", handler);
  }, []);

  // First load from global fetch (cached)
  useEffect(() => {
    if (role === "superadmin" && typeof fetchFeedbacks === "function") {
      fetchFeedbacks();
    }
  }, [role, fetchFeedbacks]);

  if (role !== "superadmin") {
    return (
      <div style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
        Access denied: Superadmin only
      </div>
    );
  }

  // Export filtered feedbacks as CSV
  const handleExport = (e) => {
    e.preventDefault();
    if (!fromDate || !toDate) {
      alert("Please select both dates");
      return;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);

    const filtered = feedbacks.filter((fb) => {
      const d = new Date(fb.createdAt);
      return d >= from && d <= to;
    });

    if (filtered.length === 0) {
      alert("No feedback found in this date range.");
      return;
    }

    const header = ["Name", "Email", "Message", "Date"];
    const rows = filtered.map((fb) => [
      '"' + (fb.name || "").replace(/"/g, '""') + '"',
      '"' + (fb.email || "").replace(/"/g, '""') + '"',
      '"' + (fb.message || "").replace(/"/g, '""') + '"',
      '"' + new Date(fb.createdAt).toLocaleString().replace(/"/g, '""') + '"',
    ]);

    const csvContent = [header, ...rows].map((r) => r.join(",")).join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `feedbacks_${fromDate}_to_${toDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this feedback?")) return;
    try {
      await deleteFeedback(id);
      if (typeof fetchFeedbacks === "function") {
        fetchFeedbacks(true); // force refresh
      }
    } catch (err) {
      alert("Error deleting feedback");
    }
  };

  const loading = feedbacksLoading;

  return (
    <div
      className={`feedback-admin-panel${darkMode ? " dark" : ""}`}
      style={{
        maxWidth: 900,
        margin: "40px auto",
        padding: 24,
        background: darkMode ? "#181a2a" : "#fff",
        borderRadius: 8,
        boxShadow: darkMode ? "0 2px 8px rgba(0,0,0,0.32)" : "0 2px 8px rgba(0,0,0,0.08)",
        color: darkMode ? "#f1f5f9" : "#222",
        transition: "background 0.2s, color 0.2s",
      }}
    >
      <h2>Feedback Submissions</h2>

      <div
        style={{
          marginBottom: 16,
          background: darkMode ? "#23263a" : "#f6f8fa",
          padding: 16,
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <form onSubmit={handleExport} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <label style={{ fontWeight: 600 }}>From:</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} required style={{ padding: 6, borderRadius: 4, border: "1px solid #ccc" }} />
          <label style={{ fontWeight: 600 }}>To:</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} required style={{ padding: 6, borderRadius: 4, border: "1px solid #ccc" }} />
          <button type="submit" style={{ background: "#007bff", color: "#fff", border: "none", borderRadius: 4, padding: "6px 16px", fontWeight: 600, cursor: "pointer" }}>
            Export
          </button>
        </form>
      </div>

      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => {
            setFromDate("");
            setToDate("");
            if (typeof fetchFeedbacks === "function") fetchFeedbacks(true);
          }}
          style={{ background: "#22c55e", color: "#fff", border: "none", borderRadius: 4, padding: "7px 22px", fontWeight: 600, cursor: "pointer", fontSize: "1rem" }}
        >
          Refresh
        </button>
      </div>

      {loading && <p>Loading...</p>}
      {!loading && feedbacks.length === 0 && <p>No feedback found.</p>}
      {!loading && feedbacks.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: darkMode ? "#23263a" : "#fff", color: darkMode ? "#f1f5f9" : "#222", minWidth: 900 }}>
            <colgroup>
              <col style={{ width: "14%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "50%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "4%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>Name</th>
                <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>Email</th>
                <th style={{ borderBottom: "1px solid #ccc", padding: 8, minWidth: 300, maxWidth: 700, wordBreak: "break-word" }}>Message</th>
                <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>Date</th>
                <th style={{ borderBottom: "1px solid #ccc", padding: 8 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {feedbacks.map((fb) => (
                <tr key={fb._id}>
                  <td style={{ borderBottom: "1px solid #333", padding: 8 }}>{fb.name}</td>
                  <td style={{ borderBottom: "1px solid #333", padding: 8 }}>{fb.email}</td>
                  <td style={{ borderBottom: "1px solid #333", padding: 8, minWidth: 300, maxWidth: 700, whiteSpace: "pre-line", wordBreak: "break-word" }}>{fb.message}</td>
                  <td style={{ borderBottom: "1px solid #333", padding: 8 }}>{new Date(fb.createdAt).toLocaleString()}</td>
                  <td style={{ borderBottom: "1px solid #333", padding: 8 }}>
                    <button style={{ background: "#e53e3e", color: "#fff", border: "none", borderRadius: 4, padding: "6px 12px", cursor: "pointer" }} onClick={() => handleDelete(fb._id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeedbackAdminPanel;